import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, FileText, Printer, Plus, Calendar, User, Stethoscope, Building2 } from 'lucide-react';
import logo from '../assets/amplicare-logo.svg';

const Confirmacion = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [requestId, setRequestId] = useState('');

  useEffect(() => {
    const storedData = sessionStorage.getItem('labRequestData');
    if (storedData) {
      setFormData(JSON.parse(storedData));
    }
    
    // Generar ID de solicitud único
    const id = Math.random().toString(36).substring(2, 10).toUpperCase();
    setRequestId(id);
  }, []);

  const handleNewRequest = () => {
    sessionStorage.removeItem('labRequestData');
    navigate('/');
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No especificado';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Amplicare Lab" 
            className="h-20 mx-auto mb-2"
          />
        </div>

        {/* Success Icon & Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-4">
            <CheckCircle2 className="w-14 h-14 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            ¡Solicitud Enviada Exitosamente!
          </h1>
          <p className="text-gray-600">
            Hemos recibido su solicitud y se ha enviado una confirmación al correo{' '}
            <span className="font-semibold text-[#505BB3]">
              {formData?.emailVeterinario || 'registrado'}
            </span>
          </p>
        </div>

        {/* Datos del Paciente Card */}
        {formData && (
          <Card className="shadow-lg mb-6 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#505BB3]" />
                <h2 className="text-lg font-semibold text-gray-800">Datos del Paciente</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Paciente</p>
                  <p className="font-semibold text-gray-800">{formData.nombrePaciente}</p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Especie</p>
                  <p className="font-semibold text-gray-800">{formData.especie}</p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Raza</p>
                  <p className="font-semibold text-gray-800">{formData.raza}</p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Edad</p>
                  <p className="font-semibold text-gray-800">{formData.edad}</p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Sexo</p>
                  <p className="font-semibold text-gray-800">{formData.sexo}</p>
                </div>
                
                {formData.propietario && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Propietario</p>
                    <p className="font-semibold text-gray-800">{formData.propietario}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Información Adicional Card */}
        {formData && (
          <Card className="shadow-lg mb-6 border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Adicional</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#505BB3] mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Fecha de la Toma</p>
                    <p className="font-medium text-gray-800">{formatDate(formData.fecha)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#505BB3] mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Hora de la Toma</p>
                    <p className="font-medium text-gray-800">{formData.horaToma}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Stethoscope className="w-5 h-5 text-[#505BB3] mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Veterinario</p>
                    <p className="font-medium text-gray-800">{formData.nombreVeterinario}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-[#505BB3] mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Clínica</p>
                    <p className="font-medium text-gray-800">{formData.clinica}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What's Next Card */}
        <Card className="shadow-lg mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#505BB3] mb-4">¿Qué sigue?</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#505BB3] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <p className="text-gray-700">
                  Procesaremos su solicitud al recibir las muestras del paciente
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#505BB3] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                <p className="text-gray-700">
                  Recibirá los resultados en el tiempo establecido según el tipo de prueba
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#505BB3] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">3</span>
                </div>
                <p className="text-gray-700">
                  Nos comunicaremos con usted si necesitamos información adicional
                </p>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="shadow-lg mb-8 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-sm text-gray-700 text-center">
              <span className="font-semibold">¿Necesita ayuda?</span> Contáctenos en{' '}
              <a 
                href="mailto:solicitudes@amplicarelab.com" 
                className="text-[#505BB3] hover:underline font-semibold"
              >
                solicitudes@amplicarelab.com
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <Button
            onClick={handleNewRequest}
            variant="outline"
            className="flex items-center gap-2 border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
          >
            <Plus className="w-4 h-4" />
            Nueva Solicitud
          </Button>
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-[#505BB3] hover:bg-[#404a99]"
          >
            <Printer className="w-4 h-4" />
            Imprimir Confirmación
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>© 2025 Amplicare Lab. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default Confirmacion;